// Admin authoring for announcements / dev patches. Compose an announcement, save it as a draft,
// then publish it — published announcements pop up once for every user on their next sign-in.

import { useRef, useState } from 'react';
import { ANNOUNCEMENT_BODY_MAX, ANNOUNCEMENT_TAGS, type Announcement, type AnnouncementTag } from '@technohull/shared';
import { EmptyState, ErrorState, InlineError, LoadingState, PageHeader, Pill, useConfirm } from '../../components/ui.js';
import { IconTrash } from '../../components/icons.js';
import { useAdminAnnouncements, useDeleteAnnouncement, useSaveAnnouncement } from '../../features/admin.js';
import { MarkdownEditor } from '../../components/MarkdownEditor.js';
import { htmlToText } from '../../lib/richtext.js';
import { renderMarkdown } from '../../lib/markdown.js';
import { useDateFormatter, useFormatDate } from '../../lib/format.js';
import { athensInputToIso, isoToAthensInput } from '../../lib/athens.js';
import { useIsCoarse } from '../../lib/media.js';
import { AdminBack } from './AdminHub.js';

interface Draft {
  id?: number;
  title: string;
  body: string;
  tag: AnnouncementTag;
  version: string;
  published: boolean;
  publishAt: string; // ISO datetime, or '' for no schedule
}
const BLANK: Draft = { title: '', body: '', tag: 'update', version: '', published: false, publishAt: '' };

// Scheduling is in Athens time, not the admin's device timezone (see lib/athens): the <input
// type="datetime-local"> shows and accepts Athens wall-clock, which we convert to/from the stored UTC
// instant on the boundary so "publish at 16:00" always means 16:00 in Greece.

const TAG_LABEL: Record<AnnouncementTag, { label: string; kind: string }> = {
  update: { label: 'Update', kind: 'info' },
  feature: { label: 'New', kind: 'accent' },
  fix: { label: 'Fix', kind: 'ok' },
  notice: { label: 'Notice', kind: 'warn' },
};

export function AdminAnnouncements() {
  const { data, isLoading, error, refetch } = useAdminAnnouncements();
  const save = useSaveAnnouncement();
  const del = useDeleteAnnouncement();
  const fmt = useFormatDate();
  const df = useDateFormatter();
  // a scheduled time is set in Athens time, so it is shown in Athens time (in the user's date format)
  const athens = (iso: string) => df.dateTime(iso, { timeZone: 'Europe/Athens' });
  const coarse = useIsCoarse();
  const [confirmNode, confirm] = useConfirm();

  const [draft, setDraft] = useState<Draft | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const list = data?.announcements ?? [];

  // the editor opens above the list: bring it into view when the tap came from further down the page
  const reveal = () => requestAnimationFrame(() => {
    const el = panelRef.current;
    const sc = el?.closest('.content');
    if (el && sc && el.getBoundingClientRect().top < sc.getBoundingClientRect().top) el.scrollIntoView({ block: 'start' });
  });
  const startNew = () => { setDraft({ ...BLANK }); reveal(); };
  const startEdit = (a: Announcement) => {
    setDraft({ id: a.id, title: a.title, body: a.body, tag: a.tag, version: a.version, published: a.published, publishAt: a.publishAt });
    reveal();
  };

  // the details count toward a limit (shown under the box): too long, and nothing is sent
  const tooLong = !!draft && draft.body.length > ANNOUNCEMENT_BODY_MAX;
  const submit = (publish?: boolean) => {
    if (!draft || !draft.title.trim() || tooLong) return;
    const payload = { ...draft, published: publish ?? draft.published };
    save.mutate(payload, { onSuccess: () => setDraft(null) });
  };

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <>
      <AdminBack />
      <PageHeader title="Announcements">
        {!draft && <button className="btn btn-sm btn-primary" onClick={startNew}>+ New announcement</button>}
      </PageHeader>

      {draft && (
        <div className="page-panel ann-panel" ref={panelRef} style={{ marginBottom: 16 }}>
          {/* Title on its own row; Type and Version side by side (both short); Schedule; then Details */}
          <div className="ann-editor">
            <label className="ann-wide">
              <span className="field-label">Title</span>
              <input
                className="field"
                placeholder="e.g. Fuel Tank import can now move unmatched vessels"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                autoFocus={!coarse} // touch: no keyboard jumping up over the form before it's read
              />
            </label>
            <label>
              <span className="field-label">Type</span>
              <select className="field" value={draft.tag} onChange={(e) => setDraft({ ...draft, tag: e.target.value as AnnouncementTag })}>
                {ANNOUNCEMENT_TAGS.map((t) => (
                  <option key={t} value={t}>{TAG_LABEL[t].label}</option>
                ))}
              </select>
            </label>
            <label>
              <span className="field-label">Version (optional)</span>
              <input className="field" placeholder="v2.1.0" value={draft.version} onChange={(e) => setDraft({ ...draft, version: e.target.value })} />
            </label>
            <label className="ann-sched">
              <span className="field-label">Schedule publish (Athens time, optional)</span>
              <input
                className="field"
                type="datetime-local"
                value={isoToAthensInput(draft.publishAt)}
                onChange={(e) => setDraft({ ...draft, publishAt: athensInputToIso(e.target.value) })}
              />
              <span className="muted ann-hint">Leave it unpublished and it goes live automatically at this Athens time.</span>
            </label>
            <div className="ann-wide">
              <span className="field-label">Details</span>
              <MarkdownEditor
                value={draft.body}
                onChange={(md) => setDraft({ ...draft, body: md })}
                placeholder="What changed, and anything users should know. Markdown supported."
                ariaLabel="Details"
                maxLength={ANNOUNCEMENT_BODY_MAX}
              />
            </div>
          </div>
          {save.isError && <InlineError error={save.error} onDismiss={() => save.reset()} />}
          {/* phones: the Published switch on its own line, then Cancel / Save draft / Publish as equal
              buttons, primary last; the footer sticks to the bottom while the editor is on screen */}
          <div className="ann-actions">
            <label className="perm-switch ann-published">
              <input type="checkbox" checked={draft.published} onChange={(e) => setDraft({ ...draft, published: e.target.checked })} />
              <span>Published (visible to users)</span>
            </label>
            <button className="btn btn-sm btn-ghost" onClick={() => setDraft(null)} disabled={save.isPending}>Cancel</button>
            <button className="btn btn-sm" onClick={() => submit(false)} disabled={save.isPending || !draft.title.trim() || tooLong} title={tooLong ? 'The details are too long to save' : undefined}>Save draft</button>
            <button className="btn btn-sm btn-primary" onClick={() => submit(true)} disabled={save.isPending || !draft.title.trim() || tooLong} title={tooLong ? 'The details are too long to save' : undefined}>
              {save.isPending ? 'Saving…' : draft.id ? 'Save & publish' : 'Publish'}
            </button>
          </div>
        </div>
      )}
      {del.isError && <InlineError error={del.error} onDismiss={() => del.reset()} />}

      {list.length === 0 ? (
        <EmptyState title="No announcements yet" hint={'Create one to greet users with a "What\'s new" popup.'} />
      ) : (
        <div className="tbl-wrap">
          {/* phones: a card per announcement: type and status, the title and a two-line excerpt, then
              "version · created" (and when a scheduled one goes live), Edit and Delete */}
          <table className="tbl tbl-cards adm-ann">
            <thead>
              <tr>
                <th style={{ width: 90 }}>Type</th>
                <th>Title</th>
                <th style={{ width: 100 }}>Version</th>
                <th style={{ width: 100 }}>Status</th>
                <th style={{ width: 120 }}>Created</th>
                <th style={{ width: 130 }} />
              </tr>
            </thead>
            <tbody>
              {list.map((a) => {
                const tag = TAG_LABEL[a.tag] ?? TAG_LABEL.update;
                const scheduled = !a.published && !!a.publishAt;
                const meta = [a.version, `created ${fmt(a.createdAt)}`, scheduled ? `goes live ${athens(a.publishAt)} (Athens)` : ''].filter(Boolean).join(' · ');
                return (
                  <tr key={a.id}>
                    <td className="n-tag"><Pill kind={tag.kind}>{tag.label}</Pill></td>
                    <td className="n-title">
                      <div className="ann-title-txt">{a.title}</div>
                      {a.body && <div className="muted ann-excerpt">{htmlToText(renderMarkdown(a.body))}</div>}
                    </td>
                    <td className="muted tc-hide">{a.version || '-'}</td>
                    <td className="tc-end n-status">{a.published ? <Pill kind="ok">Published</Pill> : scheduled ? <Pill kind="info" title={`Publishes ${athens(a.publishAt)} (Athens)`}>Scheduled</Pill> : <Pill kind="warn">Draft</Pill>}</td>
                    <td className="muted tc-hide" style={{ fontSize: 12 }}>{fmt(a.createdAt)}</td>
                    <td className="tc-meta n-meta">{meta}</td>
                    <td className="tc-actions ann-acts" style={{ textAlign: 'right' }}>
                      <button className="btn btn-sm btn-ghost" onClick={() => startEdit(a)}>Edit</button>
                      <button
                        className="btn btn-sm btn-ghost btn-del"
                        disabled={del.isPending}
                        onClick={async () => { if (await confirm({ title: 'Delete announcement', message: `Delete announcement “${a.title}”?`, confirmLabel: 'Delete', danger: true })) del.mutate(a.id); }}
                      >
                        <IconTrash size={14} /> Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {confirmNode}
    </>
  );
}
